// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Tab } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseTabList, Loading } from '~/components/common';
import {
  EstimateLineItemsTemplate,
  EstimateActivityTemplate,
  EstimateNotesTemplate,
  EstimateSummaryTemplate,
  EstimateDocumentTemplate,
} from '~/pages/template';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { EMPTY_STRING, DEFAULT_NUMBER_ONE } from '~/utils/constants/common';
import { EstimateDetailTabsEnum, RoleEnum, TypeParamEstimateDetail } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { IBaseTabItem } from '~/utils/interface/common';
import { IParamsDetailEstimate } from '~/utils/interface/estimate';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailEstimate } from '~/thunks/estimate/estimateThunk';
// Styles, images, icons
import styles from './EstimateDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const EstimateDetail = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const { hash } = useLocation();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [
      { label: EstimateDetailTabsEnum.SUMMARY, component: <EstimateSummaryTemplate /> },
    ];

    items.push({ label: EstimateDetailTabsEnum.LINE_ITEMS, component: <EstimateLineItemsTemplate /> });

    items.push({ label: EstimateDetailTabsEnum.DOCUMENTS, component: <EstimateDocumentTemplate /> });

    // items.push({ label: EstimateDetailTabsEnum.SCHEDULE, component: <EstimateScheduleTemplate /> });

    items.push({ label: EstimateDetailTabsEnum.NOTE, component: <EstimateNotesTemplate /> });

    items.push({ label: EstimateDetailTabsEnum.ACTIVITIES, component: <EstimateActivityTemplate /> });

    setTabItems(items);

  }, []);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(EstimateDetailTabsEnum.SUMMARY);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(DEFAULT_NUMBER_ONE));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);

  const handleGetEstimateDetails = useCallback((params: IParamsDetailEstimate) => {
    setIsLoading(true);

    dispatch(getDetailEstimate(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  useEffect(() => {
    if (!estimateId) return;
    const params: IParamsDetailEstimate = {
      estimateId,
      type: TypeParamEstimateDetail.SUMMARY,
    };

    handleGetEstimateDetails(params);

  }, [estimateId, handleGetEstimateDetails]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBackToEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.estimate);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.estimate);
        break;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='estimateDetailPage'
      title={t('estimate_detail_title')}
      onBackToPreviousPage={handleBackToEstimate}
    >
      <>
        <BaseTabList value={tabKey} onChange={handleTabChange}>
          {tabItems.map((item, index) => (
            <Tab
              value={item.label}
              key={index}
              label={t(`tab_label_${item.label}`)}
              className={cx('tabItem')}
            />
          ))}
        </BaseTabList>

        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default EstimateDetail;
